/* eslint no-loop-func: 0 */

import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { checkMaxLength } from "../validators/validators";
import { ACCESS_TOKEN, maxLengthFileName, maxLengthValueTableCell } from "../constants";
import { debounce } from "./throttling";
import logger from "./logger";

/**
 * Get user info from JWT token
 */
const getUserInfoFromToken = (): { authorities: { authority?: string }[] } | null => {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (!accessToken) return null;

    const decoded = jwtDecode(accessToken);
    const userInfo = decoded.sub ? JSON.parse(decoded.sub as string) : null;

    if (
      !userInfo ||
      !userInfo.authorities ||
      !Array.isArray(userInfo.authorities) ||
      userInfo.authorities.length === 0
    ) {
      return null;
    }

    return userInfo;
  } catch (error) {
    logger.error('UTIL', 'Failed to get user info from token', error);
    return null;
  }
};

/**
 * Check if the current user is an admin based on JWT token
 */
export const isUserAdmin = (): boolean => {
  const userInfo = getUserInfoFromToken();
  if (!userInfo) return false;

  return userInfo.authorities.some(
    (auth: { authority?: string }) => auth.authority === "app-admin"
  );
};

/**
 * Check if the current user is an org-admin based on JWT token
 * Org-admins can manage users within their organization (same email domain)
 */
export const isUserOrgAdmin = (): boolean => {
  const userInfo = getUserInfoFromToken();
  if (!userInfo) return false;

  return userInfo.authorities.some(
    (auth: { authority?: string }) => auth.authority === "org-admin"
  );
};

/**
 * Check if the current user can manage users (either admin or org-admin)
 */
export const canManageUsers = (): boolean => {
  return isUserAdmin() || isUserOrgAdmin();
};

export const copy = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export function copyTextToClipboard(text: string): void {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    logger.error('UTIL', 'Copy to clipboard failed', err);
  }

  document.body.removeChild(textArea);
}

const debouncedErrorFileName = debounce(() => {
  toast.error(`Name must not exceed ${maxLengthFileName} characters`);
}, 500);

const debouncedErrorLoggerName = debounce(() => {
  toast.error(`Logger must not exceed ${maxLengthFileName} characters`);
}, 500);

export const debouncedErrorMaxValueCell = debounce(() => {
  toast.error(`Value cell must not exceed ${maxLengthValueTableCell} characters`);
}, 500);

export const debouncedErrorFlowGroupName = debounce(() => {
  toast.error(`Please enter valid text`);
}, 500);

export const catchingErrorsMaxLengthLogger = (value: string, callback: (value: string) => void): void => {
  const loggerName = (value || "").trim();
  if (!loggerName && loggerName !== "") return;
  if (!checkMaxLength(maxLengthFileName, loggerName.length)) {
    debouncedErrorLoggerName();
  } else {
    callback(value);
  }
};

export const catchingErrorsMaxLengthFileName = (value: string, callback: (value: string) => void): void => {
  const fileName = (value || "").trim();
  if (!fileName && fileName !== "") return;
  if (!checkMaxLength(maxLengthFileName, fileName.length)) {
    debouncedErrorFileName();
  } else {
    callback(value);
  }
};

type WebElement = {
  xpath: string;
  fullDomPath: string;
};

type Selectors = {
  xPath: string;
  css: string;
};

type GenerationActivityResult = {
  type: string | null;
  columnName: string;
  labelName: string;
  by: string;
  recognizer: string;
  textOnElement: string;
  text: string;
  textLabel: string;
  fullDomPath: string;
  className: string | null;
  elementId: string | null;
  classes: string | null;
  containerId: string;
  containerClasses: string;
  elementType: string;
};

export const generationActivity = (event: Event, selectors: Selectors, webElement?: WebElement): GenerationActivityResult => {
  const target = event.target as HTMLElement;
  const tagName = target.tagName.toLowerCase();
  return {
    type: target.getAttribute("type"),
    columnName: "",
    labelName: "",
    by: "",
    recognizer: webElement ? webElement.xpath : selectors.xPath,
    textOnElement: tagName !== "select" ? target.innerHTML : "",
    text: tagName !== "select" ? target.innerHTML : "",
    textLabel: tagName !== "select" ? target.innerHTML : "",
    fullDomPath: webElement ? webElement.fullDomPath : selectors.css,
    className: target.getAttribute("class"),
    elementId: target.getAttribute("id"),
    classes: target.getAttribute("class"),
    containerId: ((target as any)._parent && (target as any)._parent.getAttribute("id")) || "",
    containerClasses: ((target as any)._parent && (target as any)._parent.getAttribute("class")) || "",
    elementType: tagName
  };
};
